<template>
  <div :class="{
    'is-invalid': typeof props.state !== 'undefined' && !props.state,
    'is-valid': props.state
  }">
    <AutoComplete
        :key="autoCompleteKey"
        :id="props.inputId"
        :modelValue="model"
        :suggestions="searchResults"
        :placeholder="`${t('SEARCH')}...`"
        :disabled="props.disabled"
        :loading="loading"
        :delay="250"
        :minLength="props.requiredSymbols"
        @complete="onFilter"
        @optionSelect="onSelect"
        optionLabel="label"
        class="w-100"
        fluid
    >
      <template #empty>
        <span v-if="isValidSearchQuery">{{ t("NO_RESULTS") }}</span>
        <span v-else>{{ t("ENTER_AT_LEAST_N_CHARS", {count: props.requiredSymbols}) }}</span>
      </template>
    </AutoComplete>
  </div>

  <Dialog
      :header="confirmOnSelectText"
      :draggable="false"
      :dismissable-mask="true"
      v-model:visible="confirmVisible"
      modal
      class="search-input-confirm-dialog"
      @hide="onConfirmHide"
      v-if="confirmOnSelectText"
  >
    <p class="mb-0">{{ confirmCachedValue?.label }}</p>
    <template #footer>
      <Button :label="t('CANCEL')" severity="secondary" @click="confirmVisible = false"/>
      <Button :label="t('CONFIRM')" @click="onConfirm"/>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import {computed, type ModelRef, ref} from "vue";
import {useI18n} from "vue-i18n";
import type {AutoCompleteCompleteEvent, AutoCompleteOptionSelectEvent} from "primevue";

export type Option = {
  value: string;
  label: string;
};

const {t} = useI18n();

const autoCompleteKey = ref(`autoComplete-${Date.now()}`);
const searchValue = ref("");
const searchResults = ref([]);
const loading = ref(false);

const confirmCachedValue = ref<Partial<Option>>({});
const confirmVisible = ref(false);

const model = defineModel<Option | null>() as ModelRef<Option | null>;

const props = withDefaults(defineProps<{
  onInput: (searchValue: string) => Promise<Option[]>;
  requiredSymbols: number;
  disabled: boolean;
  state?: boolean;
  inputId?: string;
  confirmOnSelectText?: string;
}>(), {
  state: undefined,
  inputId: undefined,
  disabled: false,
});

const emits = defineEmits<{
  onChange: [option: Option | null]
}>()

const onSelect = (option: AutoCompleteOptionSelectEvent) => {
  if (!option?.value) {
    return;
  }

  if (props.confirmOnSelectText) {
    confirmCachedValue.value = option.value;
    confirmVisible.value = true;
    model.value = null;
    autoCompleteKey.value = `autoComplete-${Date.now()}`;
    return;
  }

  model.value = option.value;
  emits("onChange", option.value);
};

const onConfirm = () => {
  if (confirmCachedValue.value) {
    emits("onChange", confirmCachedValue.value);
  }

  confirmVisible.value = false;
};

const onConfirmHide = () => {
  confirmVisible.value = false;
};

const isValidSearchQuery = computed(() => {
  return searchValue.value?.length >= props.requiredSymbols;
});

const onFilter = async (event: AutoCompleteCompleteEvent) => {
  searchValue.value = event.query ?? "";
  loading.value = false;
  searchResults.value = [];
  loading.value = true;

  try {
    searchResults.value = await props.onInput(searchValue.value);
  } finally {
    loading.value = false;
  }
};
</script>
